/** Photo upload for verification submissions — Firebase Storage web SDK. */
import { ref, uploadBytesResumable } from 'firebase/storage';
import { storage } from './firebase';

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** Downscales an oversized image via canvas so the upload stays under the 5MB cap. */
export async function resizeImageIfNeeded(
  file: File,
  maxBytes: number = MAX_UPLOAD_BYTES
): Promise<Blob> {
  if (file.size <= maxBytes) return file;

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Could not decode image.'));
    el.src = dataUrl;
  });

  const scale = Math.min(1, Math.max(0.2, Math.sqrt(maxBytes / file.size) * 0.9));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.85)
  );
  return blob ?? file;
}

/** Uploads to verifications/{parkingId}/{measurementId}.jpg, reporting progress in [0,1]. */
export function uploadVerificationPhoto(
  parkingId: string,
  measurementId: string,
  blob: Blob,
  onProgress?: (fraction: number) => void
): { path: string; done: Promise<void> } {
  const path = `verifications/${parkingId}/${measurementId}.jpg`;
  const storageRef = ref(storage, path);
  const task = uploadBytesResumable(storageRef, blob, { contentType: 'image/jpeg' });

  const done = new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => onProgress?.(snapshot.bytesTransferred / snapshot.totalBytes),
      reject,
      () => resolve()
    );
  });

  return { path, done };
}
